import { useCallback, useEffect, useRef, useState } from "react";

import { geocodeSearch } from "../utils/geocodeSearch";

const DEBOUNCE_MS = 350;

export function useAddressAutocomplete({ countryCode = "", provider = "default" } = {}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError]             = useState(null); // null | "network" | "empty"
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const search = useCallback((query) => {
    // Invalider immédiatement la réponse précédente, même pendant le debounce.
    abortRef.current = null;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (!query || query.length < 3) {
      setSuggestions([]);
      setIsSearching(false);
      setError(null);
      return;
    }

    setIsSearching(true);
    setError(null);

    timerRef.current = setTimeout(async () => {
      const token = {};
      abortRef.current = token;

      try {
        const results = await geocodeSearch(query, { countryCode, provider });

        if (token !== abortRef.current) return;

        setSuggestions(results);
        setError(results.length === 0 ? "empty" : null);
      } catch {
        if (token !== abortRef.current) return;
        setSuggestions([]);
        setError("network");
      } finally {
        if (token === abortRef.current) {
          setIsSearching(false);
        }
      }
    }, DEBOUNCE_MS);
  }, [countryCode, provider]);

  const clearSuggestions = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    abortRef.current = null;
    setSuggestions([]);
    setIsSearching(false);
    setError(null);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      abortRef.current = null;
    };
  }, []);

  return {
    suggestions,
    isSearching,
    error,
    search,
    clearSuggestions,
  };
}
